import { useMemo, useState } from 'react';
import type { NeedNote } from '../../../js/api/models/NeedNote';
import type { OpportunityNote } from '../../../js/api/models/OpportunityNote';
import type { PatchedNeedNote } from '../../../js/api/models/PatchedNeedNote';
import type { PatchedOpportunityNote } from '../../../js/api/models/PatchedOpportunityNote';
import type {
  ActivityNote,
  ActivityStatusHistory,
  ActivityHistory,
  UnifiedActivity,
} from '../utils/activity-types';
import { ActivityItem } from './activity-item';
import { Button } from './button';
import { Checkbox, CheckboxField } from './checkbox';
import { Label } from './fieldset';

export type NoteMutations = {
  updateNote: (
    noteId: string,
    data: PatchedOpportunityNote | PatchedNeedNote
  ) => Promise<OpportunityNote | NeedNote>;
  deleteNote: (noteId: string) => Promise<void>;
  notesQueryKey: readonly unknown[];
};

type ActivityLogProps = {
  notes: ActivityNote[] | undefined;
  statusHistory: ActivityStatusHistory[] | undefined;
  changeHistory: ActivityHistory[] | undefined;
  noteMutations?: NoteMutations;
};

/**
 * Compiles notes, status history, and change history into a unified activity list
 */
function compileActivityList(
  notes: ActivityNote[] | undefined,
  statusHistory: ActivityStatusHistory[] | undefined,
  changeHistory: ActivityHistory[] | undefined
): UnifiedActivity[] {
  const activities: UnifiedActivity[] = [];

  if (notes && notes.length > 0) {
    notes.forEach((note) => {
      activities.push({
        id: note.id,
        activity_type: 'note',
        activity_datetime: note.note_at,
        activity_object: note,
      });
    });
  }

  if (statusHistory && statusHistory.length > 0) {
    statusHistory.forEach((status) => {
      activities.push({
        id: status.id,
        activity_type: 'status',
        activity_datetime: status.change_at,
        activity_object: status,
      });
    });
  }

  if (changeHistory && changeHistory.length > 0) {
    changeHistory.forEach((change) => {
      activities.push({
        id: change.id,
        activity_type: 'update',
        activity_datetime: change.change_at,
        activity_object: change,
      });
    });
  }

  activities.sort((a, b) => {
    const dateA = new Date(a.activity_datetime).getTime();
    const dateB = new Date(b.activity_datetime).getTime();
    return dateB - dateA; // Descending order
  });

  return activities;
}

export function ActivityLog({
  notes,
  statusHistory,
  changeHistory,
  noteMutations,
}: ActivityLogProps) {
  const [showComments, setShowComments] = useState(true);
  const [showStatusChanges, setShowStatusChanges] = useState(false);
  const [showUpdates, setShowUpdates] = useState(false);
  const [sortDirection, setSortDirection] = useState<'newest' | 'oldest'>('newest');

  const activityList = useMemo(() => {
    return compileActivityList(notes, statusHistory, changeHistory);
  }, [notes, statusHistory, changeHistory]);

  const filteredAndSortedActivityList = useMemo(() => {
    let filtered = activityList.filter((activity) => {
      return (
        (activity.activity_type === 'note' && showComments) ||
        (activity.activity_type === 'status' && showStatusChanges) ||
        (activity.activity_type === 'update' && showUpdates)
      );
    });

    filtered = [...filtered].sort((a, b) => {
      const dateA = new Date(a.activity_datetime).getTime();
      const dateB = new Date(b.activity_datetime).getTime();
      return sortDirection === 'newest' ? dateB - dateA : dateA - dateB;
    });

    return filtered;
  }, [activityList, showComments, showStatusChanges, showUpdates, sortDirection]);

  return (
    <>
      {/* Filter Bar */}
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-4 tw:mb-4 tw:mt-0 tw:p-2 tw:bg-zinc-50 tw:border-b tw:border-zinc-200">
        <div className="tw:flex tw:items-center tw:gap-4">
          <CheckboxField className="tw:!gap-x-2">
            <Checkbox
              checked={showComments}
              onChange={(checked) => setShowComments(checked)}
              color="brandgreen1"
            />
            <Label>Comments</Label>
          </CheckboxField>
          <CheckboxField className="tw:!gap-x-2">
            <Checkbox
              checked={showStatusChanges}
              onChange={(checked) => setShowStatusChanges(checked)}
              color="brandgreen1"
            />
            <Label>Status Changes</Label>
          </CheckboxField>
          <CheckboxField className="tw:!gap-x-2">
            <Checkbox
              checked={showUpdates}
              onChange={(checked) => setShowUpdates(checked)}
              color="brandgreen1"
            />
            <Label>Updates</Label>
          </CheckboxField>
        </div>
        <Button
          color="brand-green-outline"
          small
          onClick={() => setSortDirection(sortDirection === 'newest' ? 'oldest' : 'newest')}
        >
          {sortDirection === 'newest' ? 'Newest First' : 'Oldest First'}
        </Button>
      </div>
      {/* Activity List */}
      <div>
        {filteredAndSortedActivityList.map((activity) => (
          <ActivityItem key={activity.id} activity={activity} noteMutations={noteMutations} />
        ))}
      </div>
    </>
  );
}
